import { Button, Drawer, message, Space } from "antd";
import { Header } from "antd/es/layout/layout";
import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
    fetchClientAddresses,
    fetchClientCart,
    fetchClientDetails,
} from "../../apis/client.api";
import { fetchWalletBalance } from "../../apis/pro_forma_invoice.api";
import { fetchUserDetails, getUserProperties } from "../../apis/user.api";
import ClientCartTable from "../../components/cart/cart_table";
import ClientCart from "../../components/cart/client_cart";
import AddClientAddressForm from "../../components/client/add_client_address_form";
import AddressList from "../../components/client/client_address_list";
import BasicClientDetails from "../../components/client/client_details/client_basic_details";
import ClientPITable from "../../components/client/client_pi_table";
import { UserProperty } from "../../components/client/client_properties";
import { ClientPropertiesDetails } from "../../components/client/client_properties/details";
import EditClientAddressForm from "../../components/client/edit_client_address_form";
import EditClientForm from "../../components/client/edit_client_form";
import { ClientVisitRecords } from "../../components/client/visit_records";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";
import { Client } from "../../interfaces/client.interface";
import ClientCouponsSection from "./client_coupons_section";
import WalletLedgerSection from "./wallet_ledger_section";

const ClientDetails = () => {
  const { hasPermission } = usePermissions();

  const { user_id } = useParams();
  const [client_data, setClientData] = useState<Client>();
  const [cart_data, setCartData] = useState<any>(null);
  const [client_addresses, setClientAddresses] = useState<any>(null);
  const [client_edit_drawer_open, setClientEditDrawerOpen] = useState(false);

  const [selected_address, setSelectedAddress] = useState(null);
  const [client_add_address_drawer_open, setClientAddAddressDrawerOpen] =
    useState(false);
  const [cart_drawer_open, setCartDrawerOpen] = useState(false);

  const [client_edit_address_drawer_open, setClientEditAddressDrawerOpen] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [clientUserProperties, setClientUserProperties] = useState<
    UserProperty[]
  >([]);
  const [, setCurrentUserDetails] = useState<any>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  const showClientEditAddressDrawer = (address: any) => {
    setClientEditAddressDrawerOpen(true);
    setSelectedAddress(address);
  };

  const onClientEditAddressClose = () => {
    setClientEditAddressDrawerOpen(false);
  };
  const fetchBasicDetails = () => {
    fetchClientDetails(user_id).then((res) => {
      setClientData(res);
    });
  };

  const fetchAddresses = () => {
    fetchClientAddresses(user_id).then((res) => {
      setClientAddresses(res);
    });
  };
  const fetchCart = () => {
    fetchClientCart(user_id).then((res) => {
      setCartData(res);
    });
  };

  useEffect(() => {
    fetchUserDetails().then(async (res) => {
      setCurrentUserDetails(res);
    });
    fetchBasicDetails();
    fetchAddresses();
    fetchCart();
    if (user_id) {
      fetchWalletBalance(user_id).then((res) => {
        setWalletBalance((res?.total_balance ?? 0) / 100);
      });
    }
  }, []);

  const fetchUserProperties = () => {
    if (!user_id) return;

    getUserProperties(user_id).then((res) => {
      setClientUserProperties(res);
    });
  };

  useEffect(() => {
    fetchUserProperties();
  }, [user_id]);

  const showClientEditDrawer = () => {
    setClientEditDrawerOpen(true);
  };

  const onClientEditDrawerClose = () => {
    setClientEditDrawerOpen(false);
  };

  const showClientAddAddressDrawer = () => {
    setClientAddAddressDrawerOpen(true);
  };

  const onClientAddAddressDrawerClose = () => {
    setClientAddAddressDrawerOpen(false);
  };

  const onCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  const downloadCartPDF = async () => {
    message.loading("Downloading PDF...");

    const pdfResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/cart-pdf/`,
      {
        params: {
          user_id: user_id,
        },
      }
    );
    const fileName = `${client_data?.user_info?.cart_number}_${client_data?.f_name}_cart.pdf`;

    const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Drawer
        title="Edit Address"
        placement="right"
        closable={false}
        onClose={onClientEditAddressClose}
        open={client_edit_address_drawer_open}
      >
        <EditClientAddressForm
          address={selected_address}
          fetchAddresses={fetchAddresses}
          onClientEditAddressClose={onClientEditAddressClose}
        />
      </Drawer>
      <Header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          width: "100%",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="primary-color-text th-font-size-20 th-font-weight-700">
            Client Details
          </div>
          <Space>
            {walletBalance !== null && walletBalance > 0 && (
              // Chip is a smooth-scroll anchor to the full ledger below — clicking it takes the
              // operator to the section that shows how this balance was arrived at (every top-up
              // and refund CN, with per-row spent / earmarked breakdown), rather than opening a
              // separate drawer. `scrollMarginTop` on the section keeps it below the sticky header.
              <a
                href="#wallet-ledger"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("wallet-ledger")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="px-3 py-1 d-inline-flex align-items-center gap-2"
                style={{
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 6,
                  lineHeight: 1,
                  cursor: "pointer",
                  textDecoration: "none",
                }}
                title="Jump to the wallet ledger below"
              >
                <span className="th-font-weight-600" style={{ color: "#389e0d", fontSize: 13 }}>
                  Wallet:
                </span>
                <span className="th-font-weight-700" style={{ color: "#389e0d", fontSize: 14 }}>
                  ₹{Number(walletBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </span>
              </a>
            )}
            <Button
              type="default"
              onClick={() => downloadCartPDF()}
              disabled={cart_data?.length < 1}
            >
              Download PDF
            </Button>
          </Space>
        </div>
      </Header>

      <div>
        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="row gap-2 my-4">
            <div className="col">
              <BasicClientDetails
                showClientEditDrawer={showClientEditDrawer}
                user_id={user_id}
                client_data={client_data ?? null}
              />
            </div>
          </div>
          <div className="my-4">
            <ClientPropertiesDetails
              properties={clientUserProperties}
              clientId={user_id}
            />
          </div>
        </div>

        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="row gap-2 my-4">
            <div className="col">
              <h4>
                Visit Records ({client_data?.user_footfall_record?.length ?? 0})
              </h4>
              <ClientVisitRecords clientDetails={client_data} />
            </div>
          </div>
        </div>

        {/* Full wallet history — the header chip scrolls here on click. Placed high on the page
            (right below Basic Details / Visit Records) so the scroll from the chip is short and
            the ledger is a first-class client attribute rather than buried below orders/PIs. */}
        <div className="m-4 p-4 px-5 white-color-bg">
          <WalletLedgerSection clientId={user_id} />
        </div>

        <div className="m-4 p-4 px-5 white-color-bg">
          <ClientCouponsSection clientId={user_id} />
        </div>

        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="row gap-2 my-4">
            <div className="col">
              <AddressList
                address_list={client_addresses ?? []}
                fetchAddresses={fetchAddresses}
                showClientAddAddressDrawer={showClientAddAddressDrawer}
                showClientEditAddressDrawer={showClientEditAddressDrawer}
              />
            </div>
          </div>
        </div>

        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="pb-3 d-flex justify-content-between">
            <div className="align-middle th-font-size-18 th-font-weight-600  primary-color-text">
              Client PIs
            </div>
          </div>
          <ClientPITable client_data={client_data} />
        </div>

        <div className="m-4 p-4 px-5 white-color-bg">
          <div className="row gap-2 my-4">
            <div className="col">
              <div className="pb-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="align-middle th-font-size-18 th-font-weight-600  primary-color-text">
                    Cart Details ({cart_data?.length ?? 0} items)
                  </div>
                  {client_data?.user_info?.cart_number && (
                    <span
                      className="px-2 py-1 d-inline-flex align-items-center gap-1"
                      style={{
                        background: "#e8f1f9",
                        border: "1px solid #91caff",
                        borderRadius: 6,
                        lineHeight: 1,
                      }}
                    >
                      <span style={{ color: "#1c77c3", fontSize: 12 }}>
                        Cart No:
                      </span>
                      <span
                        className="th-font-weight-700"
                        style={{ color: "#1c77c3", fontSize: 13 }}
                      >
                        {client_data?.user_info?.cart_number}
                      </span>
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    onClick={() => {
                      setCartDrawerOpen(true);
                    }}
                    loading={loading}
                    size="middle"
                    className="fw-bold"
                    type="primary"
                  >
                    See details
                  </Button>
                </div>
              </div>
              <ClientCartTable
                cart_items={cart_data ?? []}
                loading={loading}
                setLoading={setLoading}
              />
            </div>
          </div>
        </div>
      </div>
      <Drawer
        title="Edit Client"
        placement="right"
        closable={false}
        onClose={onClientEditDrawerClose}
        open={client_edit_drawer_open}
      >
        <EditClientForm
          client={client_data ?? {}}
          onClientEditDrawerClose={onClientEditDrawerClose}
          fetchBasicDetails={fetchBasicDetails}
          fetchExtraDetails={fetchUserProperties}
        />
      </Drawer>

      <Drawer
        title="Add Address"
        placement="right"
        onClose={onClientAddAddressDrawerClose}
        open={client_add_address_drawer_open}
        drawerStyle={{ height: "100%" }}
      >
        <AddClientAddressForm
          user_id={user_id}
          onClientAddAddressDrawerClose={onClientAddAddressDrawerClose}
          fetchAddresses={fetchAddresses}
        />
      </Drawer>

      <Drawer
        title=""
        placement="right"
        width={1000}
        closable={false}
        onClose={onCartDrawerClose}
        open={cart_drawer_open}
      >
        <ClientCart
          cart_items={cart_data ?? []}
          // createEstimate={createEstimate}
          loading={loading}
          setLoading={setLoading}
        />
      </Drawer>
    </>
  );
};

export default ClientDetails;
